class Navigation {
  /**
   * Constructor for the Navigation module.
   * @param {HTMLElement} element - The header element
   */
  constructor(element) {
    this.element = element;
    this.navbarButtons = this.element.querySelectorAll('.navbar__links--has-submenu > .navbar__links--button');
    this.mainNavigation = this.element.querySelector('.main-navigation');
    this.scrollContainer = this.mainNavigation.querySelector('.main-navigation__submenu-container');
    this.mainNavigationButtons = this.element.querySelectorAll('button.main-navigation__button, button.nav-button');
    this.mobileMenuButton = this.element.querySelector('.navbar__mobile-menu');
    this.submenuBackButtons = this.element.querySelectorAll('.main-navigation__submenu--back-button');
    this.brandAllBrandsButton = this.element.querySelectorAll('.main-navigation__brands-all-brands-button');
    this.brandSubmenuContainer = this.element.querySelector('.main-navigation__brands-submenu-container');
    this.mobileNavigationOverlay = document.querySelector('.mobile-navigation-overlay');
    this.navigationExtraMobileLinks = this.element.querySelector('.navigation-extra-mobile-links');

    this._bindEvents();
  }

  /**
   * Binds click event listeners to the navigation buttons.
   * @private
   */
  _bindEvents() {
    this.navbarButtons.forEach(button => {
      button.addEventListener('click', this._handleNavbarButtonClick.bind(this));
    });

    this.mobileMenuButton?.addEventListener('click', this._handleMobileMenuButtonClick.bind(this));

    this.mainNavigationButtons.forEach(button => {
      button.addEventListener('click', (event) => {
        this._handleSubmenuOpen(event.target);
        event.preventDefault();
      });
    });

    this.submenuBackButtons.forEach(button => {
      button.addEventListener('click', (event) => {
        this._handleSubmenuBack(event.target);
        event.preventDefault();
      });
    });

    this.brandAllBrandsButton.forEach(button => {
      button.addEventListener('click', (event) => {
        this._handleSubmenuBack(event.target);
        event.preventDefault();
      });
    });

    document.addEventListener('click', (event) => {
      // Closes any open submenu of navbars if the click is outside of a submenu
      if (!event.target.closest('.navbar__links--has-submenu')) {
        document.querySelectorAll('.navbar__links--has-submenu.open').forEach(openItem => {
          openItem.classList.remove('open');
        });
      }

      // Closes any open submenu of main navigation if the click is outside of main navigation
      if (!event.target.closest('.main-navigation')) {
        document.querySelectorAll('.main-navigation .open').forEach(openItem => {
          openItem.classList.remove('open');

          const isMainMenuButton = openItem.closest('ul')?.classList.contains('main-navigation__links');
          const submenu = openItem.querySelector('.main-navigation__submenu, .main-navigation__brands-submenu');

          // Reset menu dropdown height on closing
          if (isMainMenuButton && submenu) {
            this._setSubmenuHeight(submenu);
          }
        });

        // Remove all the current classes from buttons
        document.querySelectorAll('.main-navigation button.current').forEach(currentButton => {
          currentButton.classList.remove('current');
        });

        this._updateSubmenuOpenClass();
      }
    });

  }

  /**
   * Handles opening a submenu.
   * @param {HTMLElement} button - The submenu button that was clicked.
   * @private
   */

  _handleSubmenuOpen(button) {
    // Reset scroll position of main navigation on submenu open
    this.mainNavigation.scrollTop = 0;
    this.scrollContainer.scrollTop = 0;

    const parentUl = button.closest('ul');
    const parentLi = button.tagName.toLowerCase() === 'li' ? button : button.closest('li');

    let mainSubmenu = parentUl?.closest('.main-navigation__submenu');
    let submenuContainer = parentLi?.querySelector('.main-navigation__submenu');

    const isBrandMenu = !!parentUl.closest('.main-navigation__brands-submenu') || !!parentUl.querySelector('.main-navigation__brands-submenu');

    if (isBrandMenu) {
      mainSubmenu = parentUl?.closest('.main-navigation__brands-submenu');
    }

    const submenuItemsContainer = parentLi?.querySelector('.main-navigation__submenu--items');

    if (
      !parentLi || (
        !parentLi.querySelector('.main-navigation__submenu-container') &&
        !parentLi.querySelector('.main-navigation__brands-submenu-container') &&
        !submenuContainer
      )
    ) {
      return;
    }

    const isMainMenuButton = parentUl?.classList.contains('main-navigation__links');
    const isAlreadyOpen = parentLi.classList.contains('open');

    if (mainSubmenu) {
      // Reset menu dropdown height on closing children menus
      this._setSubmenuHeight(mainSubmenu);
    } else if (isMainMenuButton && submenuContainer && !isBrandMenu) {
      // Reset menu dropdown heights when clicking main menu buttons
      this._setSubmenuHeight(submenuContainer);
    }

    // Close all other sibling menus
    parentUl.querySelectorAll('.open').forEach(openItem => {
      if (openItem !== parentLi) {
        openItem.classList.remove('open');
        // Remove all the current classes from buttons in the closed submenu
        openItem.querySelectorAll('button.current').forEach(button => {
          button.classList.remove('current');
        });

        const brandMenu = openItem.closest('.main-navigation__brands-submenu');

        // Reset menu dropdown height on closing
        const openSubmenu = brandMenu ? brandMenu : openItem.querySelector('.main-navigation__submenu');
        const isOpenItemMainMenuButton = openItem.parentElement.classList.contains('main-navigation__links');

        if (openSubmenu && brandMenu) {
          const currentOpenSubmenu = parentLi.closest('.main-navigation__submenu--items');
          this._setSubmenuHeight(openSubmenu, currentOpenSubmenu);
        } else if (openSubmenu && isOpenItemMainMenuButton) {
          this._setSubmenuHeight(openSubmenu);
        }
      }
    });

    // Remove the 'open' class from all submenus to reset the state
    this.mainNavigation.querySelectorAll('.main-navigation__submenu.open, .main-navigation__brands-submenu.open').forEach(submenu => {
      submenu.classList.remove('open');
    });

    parentLi.classList.toggle('open');

    if (mainSubmenu && !isAlreadyOpen) {
      this._setSubmenuHeight(mainSubmenu, submenuItemsContainer);
    }

    // Add open class to the submenu that is open or its parent submenu
    // if submenu is closed. Only the "deepest" open submenu have the open class
    if (!isAlreadyOpen) {
      const newDeepestSubmenu = parentLi.querySelector('.main-navigation__brands-submenu') || parentLi.querySelector('.main-navigation__submenu');
      if (newDeepestSubmenu) {
        newDeepestSubmenu.classList.add('open');
      }
    } else {
      const parentOpenLi = parentLi.parentElement.closest('li.open');
      if (parentOpenLi) {
        const parentSubmenu = parentOpenLi.querySelector('.main-navigation__submenu');
        if (parentSubmenu) {
          parentSubmenu.classList.add('open');
        }
      }
    }

    const clickedButton = button.tagName.toLowerCase() !== 'button' ? button.closest('button') : button;
    if (clickedButton) {
      clickedButton.classList.toggle('current');
    }

    this._updateSubmenuOpenClass();
  }

  _setSubmenuHeight(submenu, submenuItemsContainer = null) {
    if (submenu) {
      const isBrandMenu = submenu.classList.contains('main-navigation__brands-submenu');
      const brandSubmenuContainer = submenu.querySelector('.main-navigation__brands-submenu--brand.open .main-navigation__submenu--items');

      const itemsContainer = isBrandMenu ? brandSubmenuContainer : submenu.querySelector('.main-navigation__submenu--items');

      const itemsContainerTitleHeight = itemsContainer?.parentNode.querySelector('.main-navigation__submenu--title') ? 72 : 0; // Title height + margin
      const submenuItemsContainerTitleHeight = submenuItemsContainer?.parentNode.querySelector('.main-navigation__submenu--title') ? 72 : 0; // Title height + margin

      const itemsContainerHeight = itemsContainer ? itemsContainer.scrollHeight + itemsContainerTitleHeight : 0;
      const submenuItemsContainerHeight = submenuItemsContainer ? submenuItemsContainer.scrollHeight + submenuItemsContainerTitleHeight : 0;

      let submenuHeight = submenuItemsContainerHeight > itemsContainerHeight ? submenuItemsContainerHeight : itemsContainerHeight;

      const brandList = submenu.querySelector('.main-navigation__brands-submenu--brands');

      if (isBrandMenu) {
        const brandMenuHeight = brandList.scrollHeight + 40;
        submenuHeight = brandMenuHeight > submenuHeight ? brandMenuHeight : submenuHeight;
      }

      if (isBrandMenu && !submenuItemsContainer && brandList) {
        submenu.style.height = brandList.scrollHeight + 104 + 'px';
      } else if (itemsContainer && (submenu.classList.contains('level-1') || submenu.classList.contains('main-navigation__brands-submenu'))) {
        submenu.style.height = submenuHeight + 48 + 'px';
      }
    }
  }

  /**
   * Handles closing a submenu with the back button.
   * @param {HTMLElement} button - The back button that was clicked.
   * @private
   */
  _handleSubmenuBack(button) {
    // Reset scroll position of main navigation on submenu open
    this.mainNavigation.scrollTop = 0;
    this.scrollContainer.scrollTop = 0;

    const parentLiToClose = button.closest('li.open');
    const brandMenu = button.closest('.main-navigation__brands-submenu');

    if (brandMenu) {
      this._setSubmenuHeight(brandMenu);
    }

    // Remove 'open' and 'current' class from all brands submenu elements
    const brandsSubmenuContainer = button.closest('.main-navigation__brands-submenu-container');
    const openSubmenus = brandsSubmenuContainer?.querySelectorAll('.open, .current');
    openSubmenus?.forEach(openItem => openItem.classList.remove('open', 'current'));

    if (parentLiToClose) {
      // Find the submenu being closed and its parent
      const submenuToClose = parentLiToClose.querySelector('.main-navigation__submenu');
      const parentOpenLi = parentLiToClose.parentElement.closest('li.open');
      const openChildren = parentLiToClose.querySelectorAll('.open, .current');
      
      // Remove 'open' and 'current' class from all child elements
      openChildren.forEach(openItem => openItem.classList.remove('open', 'current'));

      // Remove 'open' class
      if (submenuToClose) {
        submenuToClose.classList.remove('open');
      }

      parentLiToClose.classList.remove('open');

      // Set the new deepest submenu
      if (parentOpenLi && !parentLiToClose.classList.contains('main-navigation__brands-submenu--brand')) {
        const parentSubmenu = parentOpenLi.querySelector('.main-navigation__submenu');
        if (parentSubmenu) {
          parentSubmenu.classList.add('open');
        }
      }

      this._updateSubmenuOpenClass();
    }
  }

  /**
   * Checks if any submenu is open and adds or removes the 'megamenu-open' class for the main navigation.
   * @private
   */
  _updateSubmenuOpenClass() {
    const anySubmenuOpen = this.mainNavigation.querySelector('.open');
    this.mainNavigation.classList.toggle('megamenu-open', !!anySubmenuOpen);
  }

  /**
   * Handles the click event on a button of navbar.
   * @param {Event} event - The click event.
   * @private
   */
  _handleNavbarButtonClick(event) {
    const parentListItem = event.target.parentElement;

    // Close all other open submenus
    this.element.querySelectorAll('.navbar__links--has-submenu.open').forEach(openItem => {
      if (openItem !== parentListItem) {
        openItem.classList.remove('open');
      }
    });

    // Toggle the 'open' class on the current list item
    parentListItem.classList.toggle('open');
  }

  /**
   * Handles the click event on a mobile menu button.
   * @param {Event} event - The click event.
   * @private
   */
  _handleMobileMenuButtonClick(event) {
    const isMainNaviOpen = this.mainNavigation.classList.contains('mobile-open');

    this.mobileMenuButton.querySelector('.f-icon--x').classList.toggle('hidden', isMainNaviOpen);
    this.mobileMenuButton.querySelector('.f-icon--menu').classList.toggle('hidden', !isMainNaviOpen);
    this.mobileNavigationOverlay.classList.toggle('hidden', isMainNaviOpen);

    this.mainNavigation.classList.toggle('mobile-open');
    this.navigationExtraMobileLinks.classList.toggle('mobile-open');

    this._updateSubmenuOpenClass();
  }

  /**
   * Checks if the current view is desktop.
   * @returns {boolean}
   * @private
   */
  _isDesktopView() {
    return window.innerWidth >= 1180;
  }

  /**
   * Calculates the max height of direct submenus within a parent and sets all to that height.
   * @param {HTMLElement} parentElement - The <li> element containing the submenus.
   * @private
   */
  _equalizeSubmenuHeights(parentElement) {
    const submenus = parentElement.querySelectorAll('.main-navigation__submenu');
    if (submenus.length === 0) return;

    let maxHeight = 0;

    // Calculate the max height
    submenus.forEach(submenu => {
      const originalStyle = submenu.style.cssText;
      submenu.style.cssText = 'position: absolute; visibility: hidden; display: block;';

      maxHeight = Math.max(maxHeight, submenu.scrollHeight + 32);

      // Restore its original style
      submenu.style.cssText = originalStyle;
    });

    // Apply the calculated max height to all submenus
    if (maxHeight > 0) {
      submenus.forEach(submenu => {
        submenu.style.height = `${maxHeight}px`;
      });
    }
  }

  /**
   * Initializes the Navigation module.
   * @static
   */
  static init() {
    const header = document.querySelector('header');
    if (header) {
      new Navigation(header);
    }
  }
}

export default Navigation;
